import {
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  buttonVariants,
  Input,
  Label,
} from "@simplicity/ui";
import { Trash2 } from "lucide-react";
import { useContext } from "react";
import { TransformCrossRefContext } from "../TransformCrossRefContext";
import { FieldsEditor } from "./FieldsEditor";
import type { TableConfig } from "./types";

const DIRECTORY_TYPES = new Set(["iready-sftp", "file-drop"]);

/** Connector types that load only the newest file matching the pattern. */
const NEWEST_FILE_TYPES = new Set(["file-drop"]);

/** Connector types with no Primary Key: every row is numbered by its line in the file. */
const LINE_NUMBERED_TYPES = new Set(["file-drop"]);

function showDirectory(connectorType: string, table: TableConfig): boolean {
  return DIRECTORY_TYPES.has(connectorType) || table.directory !== undefined;
}

function getDependentTransformNames(
  tableName: string,
  prefix: string,
  transforms: { name: string; dependencies: { sourceTable: string }[] }[],
): string[] {
  return transforms
    .filter((t) =>
      t.dependencies.some(
        (d) =>
          d.sourceTable === `${prefix}__${tableName}__latest` ||
          d.sourceTable === `${prefix}__${tableName}__snapshots`,
      ),
    )
    .map((t) => t.name);
}

function PrimaryKeyField({
  table,
  index,
  onUpdate,
}: {
  table: TableConfig;
  index: number;
  onUpdate: (patch: Partial<TableConfig>) => void;
}) {
  if (Array.isArray(table.primaryKey)) {
    return (
      <FieldsEditor
        fields={table.primaryKey}
        onChange={(pk) => onUpdate({ primaryKey: pk })}
        label=""
        placeholder="Add key field..."
      />
    );
  }
  return (
    <Input
      id={`table-pk-${index}`}
      type="text"
      value={table.primaryKey}
      onChange={(e) => onUpdate({ primaryKey: e.target.value })}
      placeholder="e.g. id"
      className="font-mono"
    />
  );
}

function RemoveTableButton({
  tableName,
  onRemove,
  dependentTransformNames,
}: {
  tableName: string;
  onRemove: () => void;
  dependentTransformNames: string[];
}) {
  const hasWarning = dependentTransformNames.length > 0;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="compact"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove table</AlertDialogTitle>
          <AlertDialogDescription>
            Remove &quot;{tableName || "untitled"}&quot; from this connector? This cannot be undone.
          </AlertDialogDescription>
          {hasWarning && (
            <Alert variant="warning" className="mt-2">
              <p className="font-medium">Warning: This table is referenced by transforms</p>
              <ul className="mt-1 list-inside list-disc text-xs">
                {dependentTransformNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Removing this table may break these transforms.
              </p>
            </Alert>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onRemove}
            className={buttonVariants({ variant: "destructive" })}
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface FileTableRowProps {
  table: TableConfig;
  index: number;
  onChange: (table: TableConfig) => void;
  onRemove: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  connectorType: string;
}

export function FileTableRow({
  table,
  index,
  onChange,
  onRemove,
  isExpanded,
  onToggleExpand: _onToggleExpand,
  connectorType,
}: FileTableRowProps) {
  const crossRef = useContext(TransformCrossRefContext);
  const dependentTransformNames =
    crossRef && table.name
      ? getDependentTransformNames(table.name, crossRef.connectorPrefix, crossRef.transforms)
      : [];

  if (!isExpanded) return null;

  function update(patch: Partial<TableConfig>) {
    onChange({ ...table, ...patch });
  }

  const lineNumbered = LINE_NUMBERED_TYPES.has(connectorType);

  return (
    <div className="space-y-4 px-4 py-4">
      {/* A grid, so the line-numbering note takes its own row without lifting the inputs. */}
      <div
        className={`grid items-end gap-x-4 ${lineNumbered ? "grid-cols-[1fr_auto]" : "grid-cols-[1fr_1fr_auto]"}`}
      >
        <div>
          <Label htmlFor={`table-name-${index}`} className="form-label">
            Table Name
          </Label>
          <Input
            id={`table-name-${index}`}
            type="text"
            value={table.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="e.g. enrollment"
            className="font-mono"
          />
        </div>
        {!lineNumbered && (
          <div>
            <Label htmlFor={`table-pk-${index}`} className="form-label">
              Primary Key
            </Label>
            <PrimaryKeyField table={table} index={index} onUpdate={update} />
          </div>
        )}
        <RemoveTableButton
          tableName={table.name}
          onRemove={onRemove}
          dependentTransformNames={dependentTransformNames}
        />
        {lineNumbered && (
          <p className="col-span-full mt-1 text-xs text-muted-foreground">
            Rows are numbered by their line in the file.
          </p>
        )}
      </div>

      <div>
        <Label htmlFor={`table-filepattern-${index}`} className="form-label">
          File Pattern (regex)
        </Label>
        <Input
          id={`table-filepattern-${index}`}
          type="text"
          value={table.filePattern ?? ""}
          onChange={(e) => update({ filePattern: e.target.value })}
          placeholder="e.g. ^Application Data.*\.csv$"
          className="font-mono"
        />
        {NEWEST_FILE_TYPES.has(connectorType) && (
          <p className="mt-1 text-xs text-muted-foreground">
            Loads the newest file matching the pattern.
          </p>
        )}
      </div>

      {showDirectory(connectorType, table) && (
        <div>
          <Label htmlFor={`table-directory-${index}`} className="form-label">
            Directory
          </Label>
          <Input
            id={`table-directory-${index}`}
            type="text"
            value={table.directory ?? ""}
            onChange={(e) => update({ directory: e.target.value })}
            placeholder="e.g. exports/Current_Year/"
            className="font-mono"
          />
        </div>
      )}
    </div>
  );
}
